export const TASK_IDS = Object.freeze({
  REGISTER_ACCOUNT: 'REGISTER_ACCOUNT',
  COMPLETE_PROFILE: 'COMPLETE_PROFILE',
  CREATE_TOURNAMENT: 'CREATE_TOURNAMENT',
  ASSIGN_TOURNAMENT_ADMIN: 'ASSIGN_TOURNAMENT_ADMIN',
  ASSIGN_DEPARTMENT_ADMIN: 'ASSIGN_DEPARTMENT_ADMIN',
  APPROVE_PLAYERS: 'APPROVE_PLAYERS',
  ASSIGN_PLAYER_MANAGER: 'ASSIGN_PLAYER_MANAGER',
  ASSIGN_AUCTION_MANAGER: 'ASSIGN_AUCTION_MANAGER',
  ASSIGN_MATCH_MANAGER: 'ASSIGN_MATCH_MANAGER',
  INVITE_PLAYERS: 'INVITE_PLAYERS',
  CLOSE_TOURNAMENT_REGISTRATION: 'CLOSE_TOURNAMENT_REGISTRATION',
  AUCTION_DONE: 'AUCTION_DONE',
  SCHEDULE_MATCHES: 'SCHEDULE_MATCHES',
  MATCH_CONFIGURATION: 'MATCH_CONFIGURATION',
  MATCH_STARTED: 'MATCH_STARTED',
});

export const TASK_ORDER = Object.freeze([
  TASK_IDS.REGISTER_ACCOUNT,
  TASK_IDS.COMPLETE_PROFILE,
  TASK_IDS.CREATE_TOURNAMENT,
  TASK_IDS.ASSIGN_TOURNAMENT_ADMIN,
  TASK_IDS.ASSIGN_DEPARTMENT_ADMIN,
  TASK_IDS.APPROVE_PLAYERS,
  TASK_IDS.ASSIGN_PLAYER_MANAGER,
  TASK_IDS.ASSIGN_AUCTION_MANAGER,
  TASK_IDS.ASSIGN_MATCH_MANAGER,
  TASK_IDS.INVITE_PLAYERS,
  TASK_IDS.CLOSE_TOURNAMENT_REGISTRATION,
  TASK_IDS.AUCTION_DONE,
  TASK_IDS.SCHEDULE_MATCHES,
  TASK_IDS.MATCH_CONFIGURATION,
  TASK_IDS.MATCH_STARTED,
]);

const COMMON_TASKS = Object.freeze([TASK_IDS.REGISTER_ACCOUNT, TASK_IDS.COMPLETE_PROFILE]);

const ROLE_ALIASES = Object.freeze({
  INDIVIDUAL: 'INDIVIDUAL',
  individual: 'INDIVIDUAL',
  ORG_ADMIN: 'ORG_ADMIN',
  ORGANIZATION_ADMIN: 'ORG_ADMIN',
  organization_admin: 'ORG_ADMIN',
  CLUB_ADMIN: 'ORG_ADMIN',
  club_admin: 'ORG_ADMIN',
  COMMUNITY_ADMIN: 'ORG_ADMIN',
  community_admin: 'ORG_ADMIN',
  DEPARTMENT_ADMIN: 'DEPARTMENT_ADMIN',
  department_admin: 'DEPARTMENT_ADMIN',
  TOURNAMENT_ADMIN: 'TOURNAMENT_ADMIN',
  tournament_admin: 'TOURNAMENT_ADMIN',
  PLAYER_MANAGER: 'PLAYER_MANAGER',
  player_manager: 'PLAYER_MANAGER',
  AUCTION_MANAGER: 'AUCTION_MANAGER',
  auction_manager: 'AUCTION_MANAGER',
  MATCH_MANAGER: 'MATCH_MANAGER',
  match_manager: 'MATCH_MANAGER',
});

export const ROLE_TASK_MAP = Object.freeze({
  INDIVIDUAL: [TASK_IDS.CREATE_TOURNAMENT],
  ORG_ADMIN: [
    TASK_IDS.CREATE_TOURNAMENT,
    TASK_IDS.ASSIGN_TOURNAMENT_ADMIN,
    TASK_IDS.ASSIGN_DEPARTMENT_ADMIN,
  ],
  DEPARTMENT_ADMIN: [TASK_IDS.APPROVE_PLAYERS],
  TOURNAMENT_ADMIN: [
    TASK_IDS.ASSIGN_PLAYER_MANAGER,
    TASK_IDS.ASSIGN_AUCTION_MANAGER,
    TASK_IDS.ASSIGN_MATCH_MANAGER,
  ],
  PLAYER_MANAGER: [TASK_IDS.INVITE_PLAYERS, TASK_IDS.CLOSE_TOURNAMENT_REGISTRATION],
  AUCTION_MANAGER: [TASK_IDS.AUCTION_DONE],
  MATCH_MANAGER: [TASK_IDS.SCHEDULE_MATCHES, TASK_IDS.MATCH_CONFIGURATION, TASK_IDS.MATCH_STARTED],
});

export const TASK_CATALOG = Object.freeze({
  [TASK_IDS.REGISTER_ACCOUNT]: {
    id: TASK_IDS.REGISTER_ACCOUNT,
    label: 'Register account',
    description: 'Create your account to begin the tournament lifecycle.',
    route: '/auth/register',
    roleTags: ['COMMON'],
  },
  [TASK_IDS.COMPLETE_PROFILE]: {
    id: TASK_IDS.COMPLETE_PROFILE,
    label: 'Complete profile',
    description: 'Finish your profile so role assignments and actions are unlocked.',
    route: '/profile',
    roleTags: ['COMMON'],
  },
  [TASK_IDS.CREATE_TOURNAMENT]: {
    id: TASK_IDS.CREATE_TOURNAMENT,
    label: 'Create tournament',
    description: 'Set up the tournament workspace and baseline details.',
    route: '/management',
    roleTags: ['INDIVIDUAL', 'ORG_ADMIN'],
  },
  [TASK_IDS.ASSIGN_TOURNAMENT_ADMIN]: {
    id: TASK_IDS.ASSIGN_TOURNAMENT_ADMIN,
    label: 'Assign tournament admin',
    description: 'Assign a tournament admin who manages tournament operations.',
    route: '/management',
    roleTags: ['ORG_ADMIN'],
  },
  [TASK_IDS.ASSIGN_DEPARTMENT_ADMIN]: {
    id: TASK_IDS.ASSIGN_DEPARTMENT_ADMIN,
    label: 'Assign department admin',
    description: 'Assign a department admin for department-level approvals.',
    route: '/management',
    roleTags: ['ORG_ADMIN'],
  },
  [TASK_IDS.APPROVE_PLAYERS]: {
    id: TASK_IDS.APPROVE_PLAYERS,
    label: 'Approve players',
    description: 'Review and approve player participation requests.',
    route: '/management',
    roleTags: ['DEPARTMENT_ADMIN'],
  },
  [TASK_IDS.ASSIGN_PLAYER_MANAGER]: {
    id: TASK_IDS.ASSIGN_PLAYER_MANAGER,
    label: 'Assign player manager',
    description: 'Assign the player manager who controls registration workflows.',
    route: '/management',
    roleTags: ['TOURNAMENT_ADMIN'],
  },
  [TASK_IDS.ASSIGN_AUCTION_MANAGER]: {
    id: TASK_IDS.ASSIGN_AUCTION_MANAGER,
    label: 'Assign auction manager',
    description: 'Assign the auction manager who runs auction operations.',
    route: '/management',
    roleTags: ['TOURNAMENT_ADMIN'],
  },
  [TASK_IDS.ASSIGN_MATCH_MANAGER]: {
    id: TASK_IDS.ASSIGN_MATCH_MANAGER,
    label: 'Assign match manager',
    description: 'Assign the match manager responsible for fixtures and kickoff.',
    route: '/management',
    roleTags: ['TOURNAMENT_ADMIN'],
  },
  [TASK_IDS.INVITE_PLAYERS]: {
    id: TASK_IDS.INVITE_PLAYERS,
    label: 'Invite players',
    description: 'Send tournament invitations to eligible players.',
    route: '/management',
    roleTags: ['PLAYER_MANAGER'],
  },
  [TASK_IDS.CLOSE_TOURNAMENT_REGISTRATION]: {
    id: TASK_IDS.CLOSE_TOURNAMENT_REGISTRATION,
    label: 'Close tournament registration',
    description: 'Close registration once the player pool is finalized.',
    route: '/management',
    roleTags: ['PLAYER_MANAGER'],
  },
  [TASK_IDS.AUCTION_DONE]: {
    id: TASK_IDS.AUCTION_DONE,
    label: 'Auction done',
    description: 'Complete and finalize the auction process.',
    route: '/management',
    roleTags: ['AUCTION_MANAGER'],
  },
  [TASK_IDS.SCHEDULE_MATCHES]: {
    id: TASK_IDS.SCHEDULE_MATCHES,
    label: 'Schedule matches',
    description: 'Create the fixture schedule for the tournament.',
    route: '/management',
    roleTags: ['MATCH_MANAGER'],
  },
  [TASK_IDS.MATCH_CONFIGURATION]: {
    id: TASK_IDS.MATCH_CONFIGURATION,
    label: 'Match configuration',
    description: 'Configure formats, rules, and match setup details.',
    route: '/management',
    roleTags: ['MATCH_MANAGER'],
  },
  [TASK_IDS.MATCH_STARTED]: {
    id: TASK_IDS.MATCH_STARTED,
    label: 'Match started',
    description: 'Start the match and move into live operations.',
    route: '/management',
    roleTags: ['MATCH_MANAGER'],
  },
});

const CONTEXT_COMPLETION_MAP = Object.freeze({
  [TASK_IDS.REGISTER_ACCOUNT]: (context) => Boolean(context.isRegistered || context.authExists),
  [TASK_IDS.COMPLETE_PROFILE]: (context) => Boolean(context.isProfileComplete || context.profileComplete),
  [TASK_IDS.CREATE_TOURNAMENT]: (context) => Boolean(context.hasTournament || context.tournamentCreated),
  [TASK_IDS.ASSIGN_TOURNAMENT_ADMIN]: (context) => Boolean(context.assignedTournamentAdmin || context.tournamentAdminAssigned),
  [TASK_IDS.ASSIGN_DEPARTMENT_ADMIN]: (context) => Boolean(context.assignedDepartmentAdmin || context.departmentAdminAssigned),
  [TASK_IDS.APPROVE_PLAYERS]: (context) => Boolean(context.playersApproved),
  [TASK_IDS.ASSIGN_PLAYER_MANAGER]: (context) => Boolean(context.assignedPlayerManager || context.playerManagerAssigned),
  [TASK_IDS.ASSIGN_AUCTION_MANAGER]: (context) => Boolean(context.assignedAuctionManager || context.auctionManagerAssigned),
  [TASK_IDS.ASSIGN_MATCH_MANAGER]: (context) => Boolean(context.assignedMatchManager || context.matchManagerAssigned),
  [TASK_IDS.INVITE_PLAYERS]: (context) => Boolean(context.playersInvited),
  [TASK_IDS.CLOSE_TOURNAMENT_REGISTRATION]: (context) => Boolean(context.registrationClosed),
  [TASK_IDS.AUCTION_DONE]: (context) => Boolean(context.auctionDone),
  [TASK_IDS.SCHEDULE_MATCHES]: (context) => Boolean(context.matchesScheduled),
  [TASK_IDS.MATCH_CONFIGURATION]: (context) => Boolean(context.matchConfigDone || context.matchConfigured),
  [TASK_IDS.MATCH_STARTED]: (context) => Boolean(context.matchStarted),
});

const normalizeRole = (role) => {
  if (!role) return null;
  const normalized = String(role).trim();
  return ROLE_ALIASES[normalized] || ROLE_ALIASES[normalized.toUpperCase()] || null;
};

export const computeGuidance = (userRoles = [], context = {}) => {
  const canonicalRoles = Array.from(
    new Set((Array.isArray(userRoles) ? userRoles : []).map(normalizeRole).filter(Boolean))
  );

  const requiredTaskSet = new Set(COMMON_TASKS);
  canonicalRoles.forEach((role) => {
    (ROLE_TASK_MAP[role] || []).forEach((taskId) => requiredTaskSet.add(taskId));
  });

  const relevantTasksOrdered = TASK_ORDER.filter((taskId) => requiredTaskSet.has(taskId));
  const completedTasks = relevantTasksOrdered.filter((taskId) => CONTEXT_COMPLETION_MAP[taskId]?.(context));
  const pendingTasks = relevantTasksOrdered.filter((taskId) => !CONTEXT_COMPLETION_MAP[taskId]?.(context));
  const nextTask = pendingTasks[0] || null;
  const progressPercent = relevantTasksOrdered.length
    ? Math.round((completedTasks.length / relevantTasksOrdered.length) * 100)
    : 0;

  const next3Actions = pendingTasks.slice(0, 3).map((taskId) => TASK_CATALOG[taskId]);

  return {
    relevantTasksOrdered,
    completedTasks,
    pendingTasks,
    nextTask,
    next3Actions,
    progressPercent,
    completedCount: completedTasks.length,
    totalRelevantCount: relevantTasksOrdered.length,

    // Backward compatibility for existing tests/callers.
    completed: completedTasks,
    pending: pendingTasks,
    nextTaskId: nextTask,
    nextActions: next3Actions.map((task) => ({ taskId: task.id, label: task.label, route: task.route })),
    percent: progressPercent,
    relevantCount: relevantTasksOrdered.length,
  };
};
